import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/contact", label: "Contact" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        className="nyx-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
          padding: scrolled ? "12px 60px" : "20px 60px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: scrolled ? "rgba(5,5,8,0.88)" : "transparent",
          backdropFilter: scrolled ? "blur(24px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(124,58,237,0.15)" : "1px solid transparent",
          transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
        }}>

        {/* Logo */}
        <motion.div className="nyx-logo-wrap" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/")}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/favicon.svg"
            alt="NyxLabs logo"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              boxShadow: "0 0 20px rgba(124,58,237,0.4)",
              display: "block",
            }}
          />
          <span style={{
            fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22,
            background: "linear-gradient(90deg,#a855f7,#e879f9)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>NyxLabs</span>
        </motion.div>

        {/* Desktop nav */}
        <nav style={{ display: "flex", alignItems: "center", gap: 4 }} className="nyx-desktop-nav">
          {navLinks.map((link, i) => {
            const isActive = location.pathname === link.path;
            return (
              <motion.div key={link.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i + 0.3 }}>
                <Link to={link.path} style={{
                  display: "block", padding: "8px 18px", borderRadius: 8,
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: isActive ? 600 : 400, fontSize: 14, letterSpacing: "0.03em",
                  color: isActive ? "#c084fc" : "rgba(241,240,245,0.7)",
                  textDecoration: "none",
                  background: isActive ? "rgba(124,58,237,0.12)" : "transparent",
                  border: isActive ? "1px solid rgba(124,58,237,0.25)" : "1px solid transparent",
                  transition: "all 0.22s ease",
                }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.color = "#f1f0f5";
                      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.color = "rgba(241,240,245,0.7)";
                      e.currentTarget.style.background = "transparent";
                    }
                  }}>
                  {link.label}
                </Link>
              </motion.div>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="nyx-desktop-nav">
          <Link to="/home/contact" style={{ textDecoration: "none" }}>
            <motion.div
              whileHover={{ scale: 1.03, boxShadow: "0 0 24px rgba(124,58,237,0.4)" }}
              whileTap={{ scale: 0.97 }}
              style={{
                padding: "9px 22px", borderRadius: 10,
                border: "1px solid rgba(124,58,237,0.35)",
                background: "linear-gradient(135deg,rgba(124,58,237,0.15),rgba(168,85,247,0.1))",
                color: "#c084fc",
                fontFamily: "'DM Sans',sans-serif", fontWeight: 500,
                fontSize: 13, letterSpacing: "0.04em", cursor: "pointer",
              }}>
              Get in Touch
            </motion.div>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <motion.button onClick={() => setMobileOpen(!mobileOpen)} whileTap={{ scale: 0.9 }}
          className="nyx-mobile-btn"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "none" }}>
          <div style={{ width: 22, display: "flex", flexDirection: "column", gap: 5 }}>
            {[0, 1, 2].map(i => (
              <motion.span key={i}
                animate={mobileOpen
                  ? { rotate: i === 0 ? 45 : i === 2 ? -45 : 0, y: i === 0 ? 9 : i === 2 ? -9 : 0, opacity: i === 1 ? 0 : 1 }
                  : { rotate: 0, y: 0, opacity: 1 }}
                style={{
                  display: "block", height: 2, background: "#a855f7",
                  borderRadius: 2, transformOrigin: "center"
                }} />
            ))}
          </div>
        </motion.button>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28 }}
            className="nyx-mobile-menu"
            style={{
              position: "fixed", top: 70, left: 16, right: 16, zIndex: 999,
              background: "rgba(10,10,18,0.97)", backdropFilter: "blur(30px)",
              border: "1px solid rgba(124,58,237,0.2)", borderRadius: 16, padding: 16,
            }}>
            {navLinks.map((link, i) => (
              <motion.div key={link.path}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}>
                <Link to={link.path} style={{
                  display: "block", padding: "12px 16px", borderRadius: 8,
                  textDecoration: "none", fontFamily: "'DM Sans',sans-serif", fontWeight: 500,
                  color: location.pathname === link.path ? "#c084fc" : "rgba(241,240,245,0.8)",
                  background: location.pathname === link.path ? "rgba(124,58,237,0.12)" : "transparent",
                }}>{link.label}</Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .nyx-desktop-nav { display: none !important; }
          .nyx-mobile-btn  { display: flex !important; }
          .nyx-header { padding: 14px 20px !important; }
          .nyx-logo-wrap span { font-size: 18px !important; }
          .nyx-mobile-menu { top: 68px !important; left: 12px !important; right: 12px !important; }
        }
        @media (max-width: 420px) {
          .nyx-header { padding: 12px 14px !important; }
          .nyx-logo-wrap span { font-size: 16px !important; }
        }
      `}</style>
    </>
  );
}
